import { useMemo } from 'react'
import { useQuery } from 'react-query'
import useAuth from './useAuth'
import { fetchSelf, SELF_QUERY_KEY } from '../api/user/manage'
import { PATH_DASHBOARD } from '../routes/paths'

// ----------------------------------------------------------------------

// What the signed-in user may do, read from GET /person/self:
//   permissions: {can_upload, can_analyse, is_organisation_admin, can_see_suborg_analyses}
//   organisation: {id, name, is_suborganisation, parent: {id, name} | null, billing_mode}
//   suborganisations: children the user can see anything of
//   managed_organisation_ids: organisations whose users this user manages
//
// The server enforces every rule; this only decides what the UI offers. A backend
// that does not send `permissions` yet is read as full access, which is what every
// user had before per-user permissions existed. Lidya staff (is_superuser) always
// get full access.

const EMPTY_LIST = []

export default function usePermissions() {
  const { isAuthenticated, user } = useAuth()
  const isSuperuser = Boolean(user?.is_superuser)

  const query = useQuery(SELF_QUERY_KEY, fetchSelf, {
    enabled: Boolean(isAuthenticated),
    staleTime: 5 * 60 * 1000,
  })
  const self = query.data

  return useMemo(() => {
    const raw = self?.permissions
    const hasPermissionInfo = Boolean(raw)
    const canUpload = isSuperuser || !hasPermissionInfo || raw.can_upload !== false
    const canAnalyse = isSuperuser || !hasPermissionInfo || raw.can_analyse !== false
    const isOrganisationAdmin = hasPermissionInfo ? Boolean(raw.is_organisation_admin) : null
    const canSeeSuborgAnalyses = hasPermissionInfo ? Boolean(raw.can_see_suborg_analyses) : false
    const organisation = self?.organisation || null
    const suborganisations = Array.isArray(self?.suborganisations) ? self.suborganisations : EMPTY_LIST
    const managedOrganisationIds = Array.isArray(self?.managed_organisation_ids)
      ? self.managed_organisation_ids.map(String)
      : EMPTY_LIST

    return {
      self,
      // True until the first answer (or failure) arrives, so guards do not flash
      // a screen the user is about to be sent away from.
      isLoading: Boolean(isAuthenticated) && query.isLoading,
      isError: query.isError,
      hasPermissionInfo,
      isSuperuser,
      canUpload,
      canAnalyse,
      // null when the backend did not say (older backend); callers may fall back.
      isOrganisationAdmin,
      canSeeSuborgAnalyses,
      isUploadOnly: !canAnalyse,
      organisation,
      isSuborganisation: Boolean(organisation?.is_suborganisation),
      parentOrganisation: organisation?.parent || null,
      suborganisations,
      managedOrganisationIds,
      // Where the user lands when a page is not theirs to see.
      homePath: canAnalyse ? PATH_DASHBOARD.general.home : PATH_DASHBOARD.general.files,
    }
  }, [self, isSuperuser, isAuthenticated, query.isLoading, query.isError])
}

// "Parent / Child" for a sub-organisation, the plain name otherwise.
export const organisationDisplayName = (organisation, fallbackName) => {
  const name = organisation?.name || fallbackName || ''
  if (organisation?.is_suborganisation && organisation?.parent?.name && name) {
    return `${organisation.parent.name} / ${name}`
  }
  return name
}
